// ✅ P2-2: IndexedDB wrapper for SDK samples caching

import { getLogger } from '@/lib/logger/logger';

const logger = getLogger('storage:idb');

const DB_NAME = 'SdkStorage';
const DB_VERSION = 2; // Incremented for new store
const HWP_STORE_NAME = 'hwp-samples';
const HCELL_STORE_NAME = 'hcell-samples';

interface IDBConfig {
  name: string;
  version: number;
  stores: {
    name: string;
    options?: IDBObjectStoreParameters;
  }[];
}

const config: IDBConfig = {
  name: DB_NAME,
  version: DB_VERSION,
  stores: [
    {
      name: HWP_STORE_NAME,
      options: { keyPath: 'language' },
    },
    {
      name: HCELL_STORE_NAME,
      options: { keyPath: 'language' },
    },
  ],
};

class IDBManager {
  private db: IDBDatabase | null = null;

  async init(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(config.name, config.version);

      request.onerror = () => {
        const error = request.error ?? new Error('Failed to open IndexedDB');
        logger.error('[IDB] Failed to open database:', error);
        reject(error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        logger.debug('[IDB] Database opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;
        logger.debug('[IDB] Upgrading database schema...');

        // Create object stores
        config.stores.forEach(store => {
          if (!db.objectStoreNames.contains(store.name)) {
            db.createObjectStore(store.name, store.options);
            logger.debug(`[IDB] Created object store: ${store.name}`);
          }
        });
      };
    });
  }

  async get<T>(storeName: string, key: string): Promise<T | null> {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(key);

        request.onsuccess = () => {
          const result = request.result as { data?: T } | undefined;
          const data = result?.data ?? null;
          if (data) {
            logger.debug(`[IDB] Retrieved from cache: ${storeName}/${key}`);
          }
          resolve(data);
        };

        request.onerror = () => {
          const error = request.error ?? new Error('Get operation failed');
          logger.error('[IDB] Get failed:', error);
          reject(error);
        };
      });
    } catch (error) {
      logger.error('[IDB] Get error:', error);
      return null;
    }
  }

  async set(storeName: string, key: string, value: unknown): Promise<void> {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put({
          language: key,
          data: value,
          timestamp: Date.now(),
        });

        request.onsuccess = () => {
          logger.debug(`[IDB] Saved to cache: ${storeName}/${key}`);
          resolve();
        };

        request.onerror = () => {
          const error = request.error ?? new Error('Set operation failed');
          logger.error('[IDB] Set failed:', error);
          reject(error);
        };
      });
    } catch (error) {
      logger.error('[IDB] Set error:', error);
    }
  }

  async delete(storeName: string, key: string): Promise<void> {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.delete(key);

        request.onsuccess = () => {
          logger.debug(`[IDB] Deleted from cache: ${storeName}/${key}`);
          resolve();
        };

        request.onerror = () => {
          const error = request.error ?? new Error('Delete operation failed');
          logger.error('[IDB] Delete failed:', error);
          reject(error);
        };
      });
    } catch (error) {
      logger.error('[IDB] Delete error:', error);
    }
  }

  async clear(storeName: string): Promise<void> {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();

        request.onsuccess = () => {
          logger.debug(`[IDB] Cleared store: ${storeName}`);
          resolve();
        };

        request.onerror = () => {
          const error = request.error ?? new Error('Clear operation failed');
          logger.error('[IDB] Clear failed:', error);
          reject(error);
        };
      });
    } catch (error) {
      logger.error('[IDB] Clear error:', error);
    }
  }
}

// Singleton instance
export const idb = new IDBManager();

// Helper functions for HWP samples
export const hwpSamplesCache = {
  get: async (language: string) => {
    return idb.get(HWP_STORE_NAME, language);
  },

  set: async (language: string, data: unknown) => {
    return idb.set(HWP_STORE_NAME, language, data);
  },

  delete: async (language: string) => {
    return idb.delete(HWP_STORE_NAME, language);
  },

  clear: async () => {
    return idb.clear(HWP_STORE_NAME);
  },
};

// ✅ P0-2: Helper functions for HCell samples
export const hcellSamplesCache = {
  get: async (language: string) => {
    return idb.get(HCELL_STORE_NAME, language);
  },

  set: async (language: string, data: unknown) => {
    return idb.set(HCELL_STORE_NAME, language, data);
  },

  delete: async (language: string) => {
    return idb.delete(HCELL_STORE_NAME, language);
  },

  clear: async () => {
    return idb.clear(HCELL_STORE_NAME);
  },
};
